import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu as MenuIcon, X, Languages } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "../lib/LanguageContext";
import { BRAND } from "../lib/menuData";

export const Navbar = () => {
  const { t, lang, toggle } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: isHome ? "#about" : "/#about", label: t.nav.about, id: "about" },
    { href: "/menu", label: t.nav.menu, id: "menu" },
    { href: isHome ? "#gallery" : "/#gallery", label: t.nav.gallery, id: "gallery" },
    { href: isHome ? "#contact" : "/#contact", label: t.nav.contact, id: "contact" },
  ];

  const waLink = `https://wa.me/${BRAND.phone.replace("+", "")}?text=${encodeURIComponent(
    "Halo Hayalan Coffee, saya mau booking VIP Room."
  )}`;

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-[#FDFBF7]/85 border-b border-brand-border/60"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12 flex items-center justify-between h-20">
        <Link
          to="/"
          data-testid="logo-link"
          className="flex items-center gap-3 group"
        >
          <img
            src={BRAND.logo}
            alt="Hayalan Coffee Logo"
            className="w-11 h-11 rounded-full object-cover ring-1 ring-brand-border"
          />
          <div className="flex flex-col leading-none">
            <span className="font-serif text-xl md:text-2xl text-brand-ink tracking-tight">
              Hayalan
            </span>
            <span className="text-[10px] uppercase tracking-[0.32em] text-brand-blue font-medium mt-0.5">
              Coffee
            </span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {links.map((l) =>
            l.href.startsWith("/") && !l.href.includes("#") ? (
              <Link
                key={l.id}
                to={l.href}
                data-testid={`nav-${l.id}`}
                className="link-underline text-sm font-medium text-brand-ink/80 hover:text-brand-ink py-1"
              >
                {l.label}
              </Link>
            ) : (
              <a
                key={l.id}
                href={l.href}
                data-testid={`nav-${l.id}`}
                className="link-underline text-sm font-medium text-brand-ink/80 hover:text-brand-ink py-1"
              >
                {l.label}
              </a>
            )
          )}
        </nav>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={toggle}
            data-testid="lang-toggle"
            className="hidden sm:flex items-center gap-1.5 text-xs font-medium text-brand-ink/80 hover:text-brand-blue transition-colors px-3 py-2 rounded-full border border-brand-border hover:border-brand-blue/40"
            aria-label="Toggle language"
          >
            <Languages className="w-3.5 h-3.5" strokeWidth={1.8} />
            <span className="uppercase tracking-wider">
              {lang === "id" ? "ID / EN" : "EN / ID"}
            </span>
          </button>

          <a
            href={waLink}
            target="_blank"
            rel="noreferrer"
            data-testid="nav-booking-cta"
            className="hidden md:inline-flex items-center bg-brand-blue text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-blue-800 transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-brand-blue/20"
          >
            {t.nav.booking}
          </a>

          <button
            type="button"
            onClick={() => setOpen(!open)}
            className="lg:hidden p-2 text-brand-ink"
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <MenuIcon className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="lg:hidden bg-brand-cream border-t border-brand-border"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {links.map((l) => (
                <a
                  key={l.id}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${l.id}`}
                  className="py-3 text-base font-medium text-brand-ink border-b border-brand-border/50"
                >
                  {l.label}
                </a>
              ))}
              <button
                type="button"
                onClick={toggle}
                data-testid="mobile-lang-toggle"
                className="py-3 text-left text-sm font-medium text-brand-blue uppercase tracking-widest"
              >
                {lang === "id" ? "Switch to English" : "Ganti ke Indonesia"}
              </button>
              <a
                href={waLink}
                target="_blank"
                rel="noreferrer"
                data-testid="mobile-booking-cta"
                className="mt-3 inline-flex justify-center items-center bg-brand-blue text-white px-5 py-3 rounded-full text-sm font-medium"
              >
                {t.nav.booking}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
